import React from 'react';
import SectionQuiz, { QuizQuestion } from './SectionQuiz';

type Author = { name: string; fullName: string; avatar?: string };

const QUESTIONS: QuizQuestion[] = [
 {
  question: 'What will the following code print?',
  code: `const user = {
  name: "Sachin",
  greet: () => {
    console.log(this.name);
  },
};

user.greet();`,
  options: ['Sachin', 'undefined', 'null', 'Compilation Error'],
  answer: 1,
  explanation:
   'Arrow functions do not have their own `this`. They inherit `this` from the surrounding (lexical) scope — here the module/global scope, not the object. So `this.name` is `undefined`. To bind `this` to the object, use a regular method.',
 },
 {
  question: 'What is the output?',
  code: `const square = (num) => num * num;
console.log(square(4));`,
  options: ['4', '8', '16', 'undefined'],
  answer: 2,
  explanation:
   'A single-expression arrow function has an implicit return. `num * num` is returned automatically, so `square(4)` is `16`.',
 },
 {
  question: 'What does this arrow function return?',
  code: `const make = () => {
  name: "Sachin";
};

console.log(make());`,
  options: ['{ name: "Sachin" }', 'undefined', 'null', 'Compilation Error'],
  answer: 1,
  explanation:
   'With curly braces, the body is a block — not an object literal. `name:` is read as a label and the string is just an expression statement. There is no `return`, so the function returns `undefined`. To return an object implicitly, wrap it in parentheses: `() => ({ name: "Sachin" })`.',
 },
 {
  question: 'What is the result?',
  code: `const nums = [1, 2];
const more = [...nums, 3, 4];
console.log(more);`,
  options: ['[1, 2]', '[[1, 2], 3, 4]', '[1, 2, 3, 4]', 'Error'],
  answer: 2,
  explanation:
   'The spread operator `...nums` expands the elements of `nums` in place, so `more` becomes `[1, 2, 3, 4]` — a new flat array.',
 },
 {
  question: 'What gets logged?',
  code: `const base = { name: "Sachin" };
const updated = { ...base, name: "Rahul" };
console.log(updated.name);`,
  options: ['Sachin', 'Rahul', 'undefined', 'Error'],
  answer: 1,
  explanation:
   'When spreading, later properties override earlier ones. `name: "Rahul"` comes after `...base`, so it wins. `updated.name` is `"Rahul"`.',
 },
 {
  question: 'Which statement about ES6 classes is true?',
  options: [
   'They create deep copies of objects',
   'They replace prototypes entirely',
   'They are syntactic sugar over prototypes',
   'They only work in TypeScript',
  ],
  answer: 2,
  explanation:
   'ES6 classes are syntactic sugar over JavaScript’s existing prototype-based inheritance. Under the hood, methods still live on the prototype — classes just give a cleaner syntax.',
 },
 {
  question: 'What is the output?',
  code: `class User {
  static count = 0;
  constructor() {
    User.count++;
  }
}

new User();
new User();
console.log(User.count);`,
  options: ['0', '1', '2', 'Error'],
  answer: 2,
  explanation:
   '`count` is a static property shared across all instances. Each `new User()` increments it once, so after two instances `User.count` is `2`.',
 },
 {
  question: 'What is a key benefit of ES Modules?',
  options: [
   'Faster loops',
   'Automatic database access',
   'Code organization and encapsulation',
   'Smaller arrays',
  ],
  answer: 2,
  explanation:
   'Modules let you split code into self-contained units, keep internals private (encapsulation), and share only what you explicitly export — improving organization, reuse, and maintainability.',
 },
 {
  question: 'What does this template literal output?',
  code: `const name = "Sachin";
console.log(\`Hello \${name}\`);`,
  options: ['Hello ${name}', 'Hello Sachin', 'name', 'Error'],
  answer: 1,
  explanation:
   'Template literals use backticks and `${...}` interpolation. `${name}` is replaced with its value, producing `Hello Sachin`.',
 },
 {
  question: 'Which statement about default exports is true?',
  options: [
   'Multiple default exports are allowed per module',
   'They must be imported with curly braces',
   'There can be only one default export per module',
   'You cannot export classes as default',
  ],
  answer: 2,
  explanation:
   'A module may have only one default export, and it is imported without curly braces (e.g. `import User from "./User.js"`). Named exports, by contrast, allow many per module and require curly braces.',
 },
];

export const JSES6Quiz: React.FC<{ author: Author }> = ({ author }) => (
 <SectionQuiz
  title="Modern JavaScript (ES6+) Quiz"
  intro="Ten questions covering arrow functions, the spread operator, classes, template literals, and modules. Pick an answer for each, then check your score."
  questions={QUESTIONS}
  author={author}
 />
);

export default JSES6Quiz;
